import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  Modal,
  TextButton,
} from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { usePermissions } from '../../hooks/usePermissions';
import { useEntityChanged } from '../../hooks/useEntityChanged';
import { groupsApi } from '../../lib/groups';
import { DirtyHint } from '../../components/common/DirtyHint';
import type { Group } from '../../lib/groups';
import { useToastStore } from '../../stores/toast';

/**
 * 그룹 관리 페이지 (/settings/groups)
 * Owner만 접근 가능
 *
 * TODO: Phase 6 — groupsApi 스텁 → 실제 API 연동
 */
export default function GroupsSettings() {
  const navigate = useNavigate();
  const { currentMembership, fetchUser } = useAuthStore();
  const { showToast } = useToastStore();

  const companyId = currentMembership?.companyId;
  // 소유자 성질(company-rbac P4 ①) — 그룹 관리는 서버도 isOwnerMember로 판정(권한 키 없음)
  const { isOwner } = usePermissions();
  const deviceLayout = useDeviceLayout();
  const isMobile = deviceLayout === 'phone' || deviceLayout === 'fold';

  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // 그룹 추가/수정 모달 상태
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const groupNameInputRef = useRef<HTMLInputElement>(null);

  // 삭제 확인 상태
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);

  // 권한 체크
  useEffect(() => {
    if (!isOwner) {
      navigate('/');
    }
  }, [isOwner, navigate]);

  // silent: 실시간 재조회(ENTITY_CHANGED) 경로 — 목록을 「불러오는 중」으로 갈아 끼우지 않는다.
  const loadGroups = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!companyId) return;
    if (!silent) setIsLoading(true);
    try {
      const data = await groupsApi.findAll(companyId);
      setGroups(data);
    } catch {
      // Phase 6 연동 전까지 스텁 응답(빈 배열)만 반환
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // 다른 세션·다른 Owner의 그룹 생성·수정·삭제를 조용히 반영. 추가/수정 모달이 열려 있으면 미루고
  // 닫힐 때 1회 재조회(편집 중인 이름·설명을 덮어쓰지 않는다).
  useEntityChanged(['group'], () => loadGroups({ silent: true }), { paused: showCreateModal });

  const handleOpenCreate = () => {
    setEditingGroup(null);
    setGroupName('');
    setGroupDescription('');
    setShowCreateModal(true);
  };

  const handleOpenEdit = (group: Group) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setGroupDescription(group.description ?? '');
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingGroup(null);
    setGroupName('');
    setGroupDescription('');
  };

  // 수정 모드 dirty — 편집 대상 그룹의 저장된 값이 기준점이다(생성 모드는 기준점이 없어 비대상).
  // 비교는 저장이 보내는 형태(trim, 빈 설명은 미전송)와 같게.
  const isEditDirty = editingGroup
    ? groupName.trim() !== editingGroup.name ||
      groupDescription.trim() !== (editingGroup.description ?? '')
    : false;

  const handleSaveGroup = async () => {
    if (!companyId || !groupName.trim()) {
      showToast('그룹 이름을 입력해주세요.', 'error');
      return;
    }
    // 수정 모드에서만 무변경 저장을 막는다 — 생성은 기존 동작(이름만 있으면 저장) 유지.
    if (editingGroup && !isEditDirty) return;

    setIsSaving(true);
    try {
      if (editingGroup) {
        await groupsApi.update(companyId, editingGroup.id, {
          name: groupName.trim(),
          description: groupDescription.trim() || undefined,
        });
        showToast('그룹이 수정되었습니다.', 'success');
      } else {
        await groupsApi.create(companyId, {
          name: groupName.trim(),
          description: groupDescription.trim() || undefined,
        });
        showToast('그룹이 생성되었습니다.', 'success');
      }
      handleCloseModal();
      await loadGroups();
      await fetchUser();
    } catch (err) {
      const message = err instanceof Error ? err.message : '저장에 실패했습니다.';
      showToast(message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!companyId) return;

    setDeletingGroupId(groupId);
    try {
      await groupsApi.delete(companyId, groupId);
      showToast('그룹이 삭제되었습니다.', 'success');
      await loadGroups();
      await fetchUser();
    } catch (err) {
      const message = err instanceof Error ? err.message : '삭제에 실패했습니다.';
      showToast(message, 'error');
    } finally {
      setDeletingGroupId(null);
    }
  };

  return (
    <div className={isMobile ? 'space-y-4' : 'space-y-6'}>
      {/* 헤더 */}
      {isMobile ? (
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold tracking-tight text-ink-900">그룹 관리</h1>
          <Button type="button" variant="primary" size="sm" onClick={handleOpenCreate}>
            새 그룹
          </Button>
        </div>
      ) : (
        <div className="flex items-end justify-between gap-6">
          <div>
            <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">그룹 관리</h1>
            <p className="mt-1 text-[15px] font-medium text-ink-500">
              지점, 관할, 부서 등을 그룹으로 관리할 수 있습니다.
            </p>
          </div>
          <Button type="button" variant="primary" onClick={handleOpenCreate}>
            <span>+</span>
            새 그룹
          </Button>
        </div>
      )}

      {isLoading ? (
        <Card padding="xl" radius="lg" elevation="md" className="text-center text-ink-500">
          불러오는 중...
        </Card>
      ) : groups.length === 0 ? (
        <Card padding="none" radius="lg" elevation="md" className="p-10 text-center">
          <div className="mx-auto w-16 h-16 bg-ink-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <p className="text-ink-500 text-sm">
            그룹이 없습니다.
          </p>
          <p className="text-ink-400 text-xs mt-1">
            지점, 관할, 부서 등을 그룹으로 관리할 수 있습니다.
          </p>
          <Button type="button" onClick={handleOpenCreate} className="mt-4">
            첫 번째 그룹 만들기
          </Button>
        </Card>
      ) : (
        <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
          <ul className="divide-y divide-line-soft">
            {groups.map((group) => (
              <li key={group.id} className="flex items-center justify-between px-6 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink-900">{group.name}</span>
                    {group.isDefault && (
                      <Badge tone="blue" shape="rounded">
                        본점 (기본)
                      </Badge>
                    )}
                  </div>
                  {group.description && (
                    <p className="mt-0.5 text-xs text-ink-500 truncate">{group.description}</p>
                  )}
                  {group._count && (
                    <p className="mt-0.5 text-xs text-ink-400">
                      멤버 {group._count.members}명 · 현장 {group._count.sites}개
                    </p>
                  )}
                </div>
                <div className="ml-4 flex items-center gap-2 flex-shrink-0">
                  <TextButton
                    tone="neutral"
                    type="button"
                    onClick={() => handleOpenEdit(group)}
                    className="text-sm px-2 py-1 hover:bg-ink-100"
                  >
                    수정
                  </TextButton>
                  {!group.isDefault && (
                    <TextButton
                      tone="danger"
                      type="button"
                      onClick={() => handleDeleteGroup(group.id)}
                      disabled={deletingGroupId === group.id}
                      className="text-sm px-2 py-1 hover:bg-rose-50"
                    >
                      {deletingGroupId === group.id ? '삭제 중...' : '삭제'}
                    </TextButton>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* 그룹 생성/수정 모달 — 저장 중에는 닫는 길을 전부 막는다(종전엔 취소만 비활성이고 오버레이로는
          닫혔다). 이름 입력의 첫 포커스는 `initialFocusRef`로 준다 — `autoFocus`는 base가 열림 직후
          dialog로 포커스를 옮겨 덮어쓴다. 수정 모드 미저장 보호는 이번 이관 범위 밖이다(종전에도 없었다). */}
      <Modal
        open={showCreateModal}
        onClose={handleCloseModal}
        title={editingGroup ? '그룹 수정' : '새 그룹 만들기'}
        size="md"
        initialFocusRef={groupNameInputRef}
        closeOnOverlay={!isSaving}
        closeOnEsc={!isSaving}
        showCloseButton={!isSaving}
        closeLocked={isSaving}
        footer={
          <>
            {editingGroup && (
              <DirtyHint isDirty={isEditDirty} className="mr-auto text-sm" />
            )}
            <Button type="button" onClick={handleCloseModal} disabled={isSaving}>
              취소
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveGroup}
              disabled={isSaving || !groupName.trim() || (!!editingGroup && !isEditDirty)}
            >
              {isSaving ? '저장 중...' : editingGroup ? '수정' : '만들기'}
            </Button>
          </>
        }
      >
                <div className="space-y-4">
                  <div>
                    <label htmlFor="group-name" className={FORM_LABEL_CLASS}>
                      그룹 이름 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      ref={groupNameInputRef}
                      type="text"
                      id="group-name"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      disabled={isSaving}
                      className={FORM_CONTROL_CLASS}
                      placeholder="예: 강남지점, A관할, 품질관리팀"
                    />
                  </div>
                  <div>
                    <label htmlFor="group-description" className={FORM_LABEL_CLASS}>
                      설명 (선택)
                    </label>
                    <input
                      type="text"
                      id="group-description"
                      value={groupDescription}
                      onChange={(e) => setGroupDescription(e.target.value)}
                      disabled={isSaving}
                      className={FORM_CONTROL_CLASS}
                      placeholder="그룹에 대한 간략한 설명"
                    />
                  </div>
                </div>
      </Modal>
    </div>
  );
}
